import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Bg, Chip, DISPLAY, frantic, INK, popIn, UI, YELLOW } from "./theme";

/* ════════════════════════════════════════════════════════════════════════
   SCENE 1 — THE HOOK (frame 0-150 / 0-5s)
   Latar kuning menyala, notepad berantakan, teks panik + goyangan frantic.
   ════════════════════════════════════════════════════════════════════════ */

const LINES: Array<{ text: string; delay: number }> = [
  { text: "MASIH", delay: 0 },
  { text: "NYATET TRANSAKSI", delay: 5 },
  { text: "BARBERSHOP", delay: 10 },
  { text: "MANUAL?", delay: 15 },
];

const NOTES: Array<{ bar: string; amount: string; crossed?: boolean; delay: number }> = [
  { bar: "62%", amount: "35rb", delay: 26 },
  { bar: "44%", amount: "20rb", delay: 31 },
  { bar: "78%", amount: "???", delay: 36, crossed: true },
  { bar: "38%", amount: "50rb", delay: 41 },
  { bar: "70%", amount: "", delay: 46, crossed: true },
  { bar: "52%", amount: "??", delay: 51 },
];

export const Scene1Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const sh = frantic(frame);

  // Zoom-in pelan di akhir scene supaya terasa makin panik.
  const zoom = interpolate(frame, [100, 149], [1, 1.05], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ fontFamily: UI, color: INK }}>
      <Bg color={YELLOW} pattern="dots" patternOpacity={0.14}>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-7 px-[38px]"
          style={{ transform: `scale(${zoom})` }}
        >
          {/* ── Headline panik ── */}
          <div className="flex flex-col items-center gap-2">
            {LINES.map((l) => {
              const { scale, opacity } = popIn(frame, fps, l.delay, {
                damping: 8,
                stiffness: 180,
                mass: 0.8,
              });
              const wobble = Math.sin((frame - l.delay) / 3.2) * 1.6;
              return (
                <div
                  key={l.text}
                  style={{ transform: `scale(${scale}) rotate(${wobble}deg)`, opacity }}
                >
                  <div
                    className="border-4 border-black bg-white px-6 py-1 text-center text-[70px] uppercase leading-[1] text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                    style={{ fontFamily: DISPLAY, letterSpacing: "-0.01em" }}
                  >
                    {l.text}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Peringatan merah ── */}
          {(() => {
            const { scale, opacity } = popIn(frame, fps, 22, { damping: 10, stiffness: 300, mass: 0.9 });
            return (
              <div
                style={{
                  transform: `scale(${scale}) rotate(-2.2deg) translateX(${sh.x * 0.4}px)`,
                  opacity,
                }}
              >
                <div className="border-4 border-black bg-[#FF3B30] px-7 py-3 text-center uppercase leading-[1.02] text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
                  <div className="text-[48px]" style={{ fontFamily: DISPLAY }}>
                    AWAS, OMSET
                  </div>
                  <div className="text-[48px]" style={{ fontFamily: DISPLAY }}>
                    RAWAN BOCOR!
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Notepad berantakan (UI manual) ── */}
          {(() => {
            const { scale, opacity } = popIn(frame, fps, 14, { damping: 12, stiffness: 220, mass: 0.9 });
            return (
              <div
                style={{
                  transform: `scale(${scale}) rotate(${2.2 + sh.rot}deg) translate(${sh.x}px, ${sh.y}px)`,
                  opacity,
                }}
                className="relative w-[900px]"
              >
                <div className="border-4 border-black bg-white shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
                  {/* header buku catatan */}
                  <div className="flex items-center justify-between border-b-4 border-black bg-black px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-6 rounded-full border-4 border-white bg-[#FF3B30]" />
                      <div className="h-6 w-6 rounded-full border-4 border-white bg-[#FFE14D]" />
                    </div>
                    <div
                      className="text-[32px] uppercase tracking-[0.16em] text-white"
                      style={{ fontFamily: DISPLAY }}
                    >
                      CATATAN HARIAN
                    </div>
                    <div className="h-6 w-6 rounded-full border-4 border-white bg-[#B8FF2F]" />
                  </div>

                  {/* kertas bergaris */}
                  <div
                    className="relative px-8 py-6"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(transparent 0 54px, #c3c9d4 54px 56px)",
                    }}
                  >
                    <div className="flex flex-col gap-2 pt-1">
                      {NOTES.map((n) => {
                        const { scale: ns, opacity: no } = popIn(frame, fps, n.delay, {
                          damping: 9,
                          stiffness: 340,
                          mass: 0.7,
                        });
                        return (
                          <div
                            key={n.bar + n.amount + n.delay}
                            className="flex items-center justify-between"
                            style={{
                              height: 56,
                              transform: `scale(${ns}) translateX(${Math.sin((frame + n.delay) / 2.4) * 6}px)`,
                              opacity: no,
                            }}
                          >
                            <div className="relative h-4 w-[540px]">
                              <div
                                className="absolute inset-y-0 left-0 bg-[#8e9aaf]"
                                style={{ width: n.bar, transform: "rotate(-0.8deg)" }}
                              />
                              {n.crossed ? (
                                <div
                                  className="absolute top-1/2 h-[6px] w-full bg-[#FF3B30]"
                                  style={{ transform: "rotate(2.4deg)" }}
                                />
                              ) : null}
                            </div>
                            <div className="text-[38px] text-[#FF3B30]" style={{ fontFamily: DISPLAY }}>
                              {n.amount}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* stempel merah — di pojok atas, tidak menutupi nominal */}
                    {(() => {
                      const st = spring({
                        frame: frame - 62,
                        fps,
                        config: { damping: 12, stiffness: 400, mass: 0.8 },
                      });
                      const so = interpolate(frame, [62, 67], [0, 1], {
                        extrapolateLeft: "clamp",
                        extrapolateRight: "clamp",
                      });
                      return (
                        <div
                          className="absolute -right-4 -top-6"
                          style={{
                            transform: `scale(${interpolate(st, [0, 1], [2.6, 1])}) rotate(-14deg)`,
                            opacity: so,
                          }}
                        >
                          <div className="border-[6px] border-[#FF3B30] bg-white/80 px-4 py-1">
                            <div
                              className="text-[40px] uppercase leading-none text-[#FF3B30]"
                              style={{ fontFamily: DISPLAY }}
                            >
                              BOCOR!
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* kertas lepas yang terbang */}
                {[
                  { x: -46, y: -34, r: -12, d: 44 },
                  { x: 770, y: 470, r: 14, d: 50 },
                ].map((p) => {
                  const { scale: ps, opacity: po } = popIn(frame, fps, p.d, {
                    damping: 10,
                    stiffness: 260,
                    mass: 0.8,
                  });
                  return (
                    <div
                      key={p.x + "" + p.y}
                      className="absolute border-4 border-black bg-white px-4 py-2 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                      style={{
                        left: p.x,
                        top: p.y,
                        transform: `scale(${ps}) rotate(${p.r + Math.sin((frame + p.d) / 3) * 4}deg)`,
                        opacity: po,
                      }}
                    >
                      <div className="text-[28px] text-[#8e9aaf]" style={{ fontFamily: DISPLAY }}>
                        Rp ...
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ── Chip bawah ── */}
          <div>
            <Chip className="bg-black text-[28px] text-[#FFE14D]">
              Barbershop · 1 Shift · 8 Kapster
            </Chip>
          </div>
        </div>
      </Bg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
